import { useSnapshot } from 'valtio'
import { TenantConfigState } from '@app/storage'

export const TAWKTO_POSITIONS = [
  'bottom-right-above-menu',
  'bottom-left-above-menu',
  'bottom-right-raw',
  'main-menu',
  'vip-subscribe',
] as const

export type TawktoPosition = (typeof TAWKTO_POSITIONS)[number]

/** Positions that render the chat inside the nav menu overlay (no floating bubble). */
const MENU_POSITIONS: readonly TawktoPosition[] = ['main-menu', 'vip-subscribe']

/** Positions that render the floating chat bubble. */
const BUBBLE_POSITIONS: readonly TawktoPosition[] = [
  'bottom-right-above-menu',
  'bottom-left-above-menu',
  'bottom-right-raw',
]

/** Returns true if ANY of the active positions renders chat in the nav menu. */
export const isSupportChatMenuPosition = (positions: TawktoPosition[]): boolean =>
  positions.some(p => MENU_POSITIONS.includes(p))

/** Returns true if the vip-subscribe mode is active (subscribe button → chat). */
export const isVipSubscribeMode = (positions: TawktoPosition[]): boolean =>
  positions.includes('vip-subscribe')

/** Returns the first active floating-bubble position, or null if none. */
export const getBubblePosition = (positions: TawktoPosition[]): TawktoPosition | null =>
  positions.find(p => (BUBBLE_POSITIONS as readonly string[]).includes(p)) ?? null

const isValidPosition = (v: unknown): v is TawktoPosition =>
  typeof v === 'string' && (TAWKTO_POSITIONS as readonly string[]).includes(v)

/**
 * Parses the `positions` or legacy `position` field from feature-flag metadata.
 * Supports:
 *  - Array:  `positions: ["bottom-right-above-menu", "vip-subscribe"]`
 *  - String: `position: "vip-subscribe"` (legacy single value, still supported)
 * Falls back to `['bottom-right-above-menu']` when nothing valid is found.
 */
const parsePositions = (metadata: Record<string, unknown>): TawktoPosition[] => {
  // New: array form
  if (Array.isArray(metadata.positions)) {
    const valid = metadata.positions.filter(isValidPosition)
    if (valid.length > 0) return valid
  }
  // Legacy: single string
  if (isValidPosition(metadata.position)) {
    return [metadata.position]
  }
  return ['bottom-right-above-menu']
}

export interface TawktoConfig {
  propertyId: string
  widgetId: string
  /** Active widget positions. Can include multiple values, e.g. ["bottom-right-above-menu", "vip-subscribe"]. */
  positions: TawktoPosition[]
  /**
   * When non-empty, launcher is shown only on these routes (allowlist).
   * Takes precedence over hiddenRoutes.
   */
  allowedRoutes: string[]
  /** Blocklist — ignored when allowedRoutes is set. */
  hiddenRoutes: string[]
  /**
   * Custom label for the VIP subscribe button when 'vip-subscribe' is in positions.
   * Falls back to the default i18n key when not set.
   */
  vipSubscribeButtonLabel?: string
}

const parseRouteList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((r): r is string => typeof r === 'string' && r.length > 0) : []

const normalizeMetadata = (raw: unknown): Record<string, unknown> | null => {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return null
}

/**
 * Reads the support_chat_tawkto feature flag directly from TenantConfigState.
 * Returns typed config or null when the flag is absent / disabled / misconfigured.
 * Uses a direct array find rather than the path resolver so there are no warnings
 * when the flag row doesn't exist yet in the DB for a given tenant.
 */
export function useTawktoConfig(): TawktoConfig | null {
  const { featureFlags } = useSnapshot(TenantConfigState)

  const flag = featureFlags?.find(f => f.key === 'support_chat_tawkto')
  const metadata = normalizeMetadata(flag?.metadata)

  if (!flag?.enabled || !metadata?.propertyId || !metadata?.widgetId) return null

  return {
    propertyId:   String(metadata.propertyId),
    widgetId:     String(metadata.widgetId),
    positions:    parsePositions(metadata),
    allowedRoutes: parseRouteList(metadata.allowedRoutes),
    hiddenRoutes:  parseRouteList(metadata.hiddenRoutes),
    vipSubscribeButtonLabel: typeof metadata.vipSubscribeButtonLabel === 'string'
      ? metadata.vipSubscribeButtonLabel
      : undefined,
  }
}
